import { z } from 'zod';
import { isCalendarDate, normalizeName } from './people.js';
import { localizedTitlesSchema, type LocalizedTitles } from './training.js';

export const assessmentValueTypes = ['numeric', 'duration', 'boolean', 'rubric'] as const;
export type AssessmentValueType = (typeof assessmentValueTypes)[number];

export const assessmentDirections = ['lower_is_better', 'higher_is_better'] as const;
export type AssessmentDirection = (typeof assessmentDirections)[number];

export const assessmentAggregations = ['best', 'mean', 'last', 'coach_selected'] as const;
export type AssessmentAggregation = (typeof assessmentAggregations)[number];

export const attemptStatuses = ['valid', 'invalid', 'not_attempted'] as const;
export type AttemptStatus = (typeof attemptStatuses)[number];

export const resultPublicationStates = ['draft', 'published', 'archived'] as const;
export type ResultPublicationState = (typeof resultPublicationStates)[number];

const shortText = (maximum: number) =>
  z.string().transform(normalizeName).pipe(z.string().max(maximum));
const optionalText = (maximum: number) =>
  shortText(maximum)
    .transform((value) => (value === '' ? null : value))
    .nullable();

const decimalPattern = /^-?\d{1,15}(\.\d{1,15})?$/;

/** A measured value as decimal text; plain JSON numbers are accepted and turned into text. */
const decimalValue = z
  .union([z.number(), z.string()])
  .transform((value) => String(value).trim())
  .pipe(z.string().regex(decimalPattern));

const calendarDateSchema = z.string().refine(isCalendarDate);

/** Club-authored text in up to three languages; any may be omitted and the others fall back. */
const localizedTexts = (maximum: number) =>
  z.strictObject({
    en: shortText(maximum).pipe(z.string().min(1)).optional(),
    hu: shortText(maximum).pipe(z.string().min(1)).optional(),
    ro: shortText(maximum).pipe(z.string().min(1)).optional(),
  });

const rubricLevelSchema = z.strictObject({
  descriptions: localizedTexts(300).refine(
    (texts) => texts.en !== undefined || texts.hu !== undefined || texts.ro !== undefined,
  ),
});
export type RubricLevelInput = z.infer<typeof rubricLevelSchema>;

const protocolContextSchema = z
  .strictObject({
    /** For example `30 m`; a changed distance starts a new comparable series. */
    distance: optionalText(100).default(null),
    course: optionalText(500).default(null),
    surface: optionalText(100).default(null),
    method: optionalText(500).default(null),
  })
  .default({ distance: null, course: null, surface: null, method: null });

const definitionVersionFields = {
  titles: localizedTitlesSchema,
  instructions: localizedTexts(2000).nullable().default(null),
  valueType: z.enum(assessmentValueTypes),
  /** The canonical unit; every duration is in `seconds`. Yes/no and rubric results have none. */
  unit: optionalText(30).default(null),
  precision: z.number().int().min(0).max(4).default(0),
  minimum: decimalValue.nullable().default(null),
  maximum: decimalValue.nullable().default(null),
  direction: z.enum(assessmentDirections).default('higher_is_better'),
  attemptsAllowed: z.number().int().min(1).max(20).default(1),
  aggregation: z.enum(assessmentAggregations).default('best'),
  /** Lets measurements outside the bounds through; a duration is never negative regardless. */
  allowOutOfRange: z.boolean().default(false),
  /** Whether percent change is meaningful; defaults to true for durations and false otherwise. */
  ratioScale: z.boolean().optional(),
  /** Levels 1..n in order; required for a rubric and not allowed otherwise. */
  rubricLevels: z.array(rubricLevelSchema).min(2).max(10).nullable().default(null),
  protocol: protocolContextSchema,
};

export const createDefinitionRequestSchema = z.strictObject({
  /** Null makes the protocol available to every department of the club. */
  departmentId: z.uuid().nullable().default(null),
  /** Stable key such as `skating-sprint`; versions of one protocol share it. */
  code: z
    .string()
    .trim()
    .toLowerCase()
    .max(60)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/),
  ...definitionVersionFields,
});
export type CreateDefinitionRequest = z.infer<typeof createDefinitionRequestSchema>;

/** A new version replaces the whole content; earlier versions and the sessions using them stay as they were. */
export const newDefinitionVersionRequestSchema = z.strictObject(definitionVersionFields);
export type NewDefinitionVersionRequest = z.infer<typeof newDefinitionVersionRequestSchema>;

export const createSessionRequestSchema = z.strictObject({
  departmentId: z.uuid(),
  sessionDate: calendarDateSchema,
  /** For example the venue, surface conditions or the group assessed; shown to staff only. */
  context: optionalText(500).default(null),
  /** An assessment event of the calendar this session belongs to. */
  eventId: z.uuid().nullable().default(null),
  definitionVersionIds: z
    .array(z.uuid())
    .min(1)
    .max(20)
    .refine((identifiers) => new Set(identifiers).size === identifiers.length),
  athleteIds: z
    .array(z.uuid())
    .min(1)
    .max(200)
    .refine((identifiers) => new Set(identifiers).size === identifiers.length),
});
export type CreateSessionRequest = z.infer<typeof createSessionRequestSchema>;

const attemptSchema = z.strictObject({
  status: z.enum(attemptStatuses),
  /** Absent for invalid and not-attempted tries; zero is a real measurement and stays valid. */
  value: z
    .union([decimalValue, z.boolean()])
    .transform((value) => (typeof value === 'boolean' ? (value ? '1' : '0') : value))
    .nullable()
    .default(null),
  /** A short note that is safe to keep: no names, health or other personal details. */
  note: optionalText(200).default(null),
});
export type AttemptInputBody = z.infer<typeof attemptSchema>;

const resultContentFields = {
  /** The unit the values are in; refused unless it is the definition's canonical unit. */
  unit: optionalText(30).default(null),
  attempts: z.array(attemptSchema).min(1).max(20),
  /** Required with a coach-selected aggregation, and refused with any other. */
  selectedAttemptNumber: z.number().int().min(1).max(20).nullable().default(null),
  selectionReason: optionalText(500).default(null),
  /** Staff only; never leaves the server in a family view. */
  privateNote: optionalText(2000).default(null),
  /** The only comment families see, and only after publication. */
  publicComment: optionalText(500).default(null),
};

export const resultRowSchema = z.strictObject({
  athleteId: z.uuid(),
  definitionVersionId: z.uuid(),
  /** The row version the coach saw; zero when no result exists yet. */
  version: z.number().int().min(0),
  ...resultContentFields,
});
export type ResultRowInput = z.infer<typeof resultRowSchema>;

export const updateResultsRequestSchema = z.strictObject({
  items: z
    .array(resultRowSchema)
    .min(1)
    .max(200)
    .refine(
      (rows) =>
        new Set(rows.map((row) => `${row.athleteId}|${row.definitionVersionId}`)).size ===
        rows.length,
    ),
});
export type UpdateResultsRequest = z.infer<typeof updateResultsRequestSchema>;

/** A published result is changed only through a correction, which makes a new draft revision. */
export const correctResultRequestSchema = z.strictObject({
  athleteId: z.uuid(),
  definitionVersionId: z.uuid(),
  correctionReason: shortText(500).pipe(z.string().min(1)),
  ...resultContentFields,
});
export type CorrectResultRequest = z.infer<typeof correctResultRequestSchema>;

/** Discards the unpublished correction of one result; the published revision stays as it is. */
export const discardCorrectionRequestSchema = z.strictObject({
  athleteId: z.uuid(),
  definitionVersionId: z.uuid(),
  /** The row version the coach saw. */
  version: z.number().int().min(1),
});
export type DiscardCorrectionRequest = z.infer<typeof discardCorrectionRequestSchema>;

export const publishResultsRequestSchema = z.strictObject({
  /** Limits publication to these athletes; omitted means every draft of the session. */
  athleteIds: z.array(z.uuid()).min(1).max(200).optional(),
});
export type PublishResultsRequest = z.infer<typeof publishResultsRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------------------------

export interface LocalizedTexts {
  en?: string | undefined;
  hu?: string | undefined;
  ro?: string | undefined;
}

export interface ProtocolContextView {
  distance: string | null;
  course: string | null;
  surface: string | null;
  method: string | null;
}

export interface RubricLevelView {
  level: number;
  descriptions: LocalizedTexts;
}

export interface DefinitionVersionView {
  versionId: string;
  versionNumber: number;
  titles: LocalizedTitles;
  instructions: LocalizedTexts | null;
  valueType: AssessmentValueType;
  unit: string | null;
  precision: number;
  minimum: string | null;
  maximum: string | null;
  direction: AssessmentDirection;
  attemptsAllowed: number;
  aggregation: AssessmentAggregation;
  allowOutOfRange: boolean;
  ratioScale: boolean;
  rubricLevels: RubricLevelView[] | null;
  protocol: ProtocolContextView;
  /** Results join in a chart only when this matches. */
  comparabilityKey: string;
}

export interface DefinitionView extends DefinitionVersionView {
  id: string;
  departmentId: string | null;
  sportCode: string;
  code: string;
  status: 'active' | 'archived';
  /** Every version of the protocol, newest first; the content above is the newest. */
  versions: { versionId: string; versionNumber: number }[];
}

export interface AttemptView {
  attemptNumber: number;
  status: AttemptStatus;
  value: string | null;
  note: string | null;
}

/** A revision as staff see it, with everything recorded. Families never receive this shape. */
export interface RevisionView {
  revisionId: string;
  revisionNumber: number;
  status: AttemptStatus;
  selectedValue: string | null;
  selectedAttemptNumber: number | null;
  aggregation: AssessmentAggregation;
  selectionReason: string | null;
  publicationState: ResultPublicationState;
  correctionReason: string | null;
  privateNote: string | null;
  publicComment: string | null;
  publishedAt: string | null;
  attempts: AttemptView[];
}

export interface ResultView {
  resultId: string;
  athleteId: string;
  definitionVersionId: string;
  /** Counts changes to the draft; send it back when saving. */
  version: number;
  draft: RevisionView | null;
  published: RevisionView | null;
}

export interface SessionProtocolView {
  definitionVersionId: string;
  definitionId: string;
  code: string;
  versionNumber: number;
  titles: LocalizedTitles;
  valueType: AssessmentValueType;
  unit: string | null;
  precision: number;
  minimum: string | null;
  maximum: string | null;
  direction: AssessmentDirection;
  attemptsAllowed: number;
  aggregation: AssessmentAggregation;
  allowOutOfRange: boolean;
  rubricLevels: RubricLevelView[] | null;
  protocol: ProtocolContextView;
}

export interface SessionParticipantView {
  athleteId: string;
  givenName: string;
  familyName: string;
}

export interface GroupAggregateView {
  definitionVersionId: string;
  /** Participants with a valid current result. */
  count: number;
  /** True when fewer than five participants have a result; no figures are then given. */
  suppressed: boolean;
  minimum: string | null;
  maximum: string | null;
  mean: string | null;
}

export interface SessionSummaryView {
  id: string;
  departmentId: string;
  sessionDate: string;
  context: string | null;
  eventId: string | null;
  participantCount: number;
  protocolCount: number;
  draftCount: number;
  publishedCount: number;
}

export interface SessionView {
  id: string;
  departmentId: string;
  sessionDate: string;
  context: string | null;
  eventId: string | null;
  version: number;
  protocols: SessionProtocolView[];
  participants: SessionParticipantView[];
  results: ResultView[];
  /** Staff only; never a ranking, and suppressed below five participants. */
  aggregates: GroupAggregateView[];
}

/** One published result in an export. Drafts, raw attempts and private notes are never part of it. */
export interface SessionExportRowView {
  givenName: string;
  familyName: string;
  protocolCode: string;
  protocolVersion: number;
  titles: LocalizedTitles;
  unit: string | null;
  status: AttemptStatus;
  value: string | null;
  revisionNumber: number;
  publishedAt: string | null;
  publicComment: string | null;
}

/** The data of a staff export; the client labels the columns in the reader's language. */
export interface SessionExportView {
  sessionDate: string;
  rows: SessionExportRowView[];
}

export type ResultRowOutcome = 'saved' | 'conflict' | 'rejected' | 'published' | 'not_found';

export interface ResultProblemView {
  /** Null for a problem with the row as a whole. */
  attemptNumber: number | null;
  field: string;
  messageKey: string;
}

export interface ResultRowResultView {
  athleteId: string;
  definitionVersionId: string;
  outcome: ResultRowOutcome;
  /** The stored result after the change, or the current one on a conflict. */
  result: ResultView | null;
  problems: ResultProblemView[];
}

export interface ResultBatchView {
  results: ResultRowResultView[];
  saved: number;
  conflicts: number;
  rejected: number;
}

export interface PublishResultsView {
  published: number;
  /** Drafts that were not published, for example because the athlete was not named. */
  skipped: number;
}

/** A published point of an athlete's progress, without raw attempts or private notes. */
export interface ProgressPointView {
  sessionId: string;
  sessionDate: string;
  definitionVersionId: string;
  versionNumber: number;
  revisionNumber: number;
  status: AttemptStatus;
  /** Null when nothing valid was recorded; never zero in its place. */
  value: string | null;
  publicComment: string | null;
}

export interface ProgressChangeView {
  baselineDate: string;
  baselineValue: string;
  latestDate: string;
  latestValue: string;
  /** Latest minus baseline in the series' unit. */
  absolute: string;
  /** Improvement in percent in the series' direction; null when only the absolute change applies. */
  percent: number | null;
}

export interface ProgressSeriesView {
  comparabilityKey: string;
  code: string;
  titles: LocalizedTitles;
  valueType: AssessmentValueType;
  unit: string | null;
  precision: number;
  direction: AssessmentDirection;
  ratioScale: boolean;
  rubricLevels: RubricLevelView[] | null;
  protocol: ProtocolContextView;
  points: ProgressPointView[];
  change: ProgressChangeView | null;
}

export interface ProgressView {
  athleteId: string;
  series: ProgressSeriesView[];
}
